import React, { useContext, useEffect, useRef, useState } from 'react';
import { ShopContext } from '../context/shopcontext';
import { FaSearch } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';

const SearchBar = () => {
  const {
    search,
    setSearch,
    showSearch,
    fetchSearchSuggestions,
    searchSuggestions,
    setSearchSuggestions,
  } = useContext(ShopContext);

  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const inputRef = useRef(null);
  const dropdownRef = useRef(null);
  const navigate = useNavigate();
  const debounceTimeout = useRef(null);

  // Debounced search
  useEffect(() => {
    clearTimeout(debounceTimeout.current);
    debounceTimeout.current = setTimeout(() => {
      if (search.trim().length >= 2) {
        fetchSearchSuggestions(search);
      } else {
        setSearchSuggestions([]);
      }
    }, 300);
    return () => clearTimeout(debounceTimeout.current);
  }, [search]);

  // Click outside to close
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target) &&
        inputRef.current &&
        !inputRef.current.contains(e.target)
      ) {
        setSearchSuggestions([]);
        setHighlightedIndex(-1);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const goToProduct = (id) => {
    navigate(`/product/${id}`);
    setSearch('');
    setSearchSuggestions([]);
    setHighlightedIndex(-1);
    inputRef.current?.blur();
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleKeyDown = (e) => {
    if (!searchSuggestions.length) return;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightedIndex((prev) => (prev + 1) % searchSuggestions.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightedIndex((prev) => (prev <= 0 ? searchSuggestions.length - 1 : prev - 1));
    } else if (e.key === 'Enter' && highlightedIndex >= 0) {
      e.preventDefault();
      goToProduct(searchSuggestions[highlightedIndex]._id);
    } else if (e.key === 'Escape') {
      setSearchSuggestions([]);
      setHighlightedIndex(-1);
    }
  };

  const handleClear = () => {
    setSearchSuggestions([]);
    setHighlightedIndex(-1);
  };

  if (!showSearch) return null;

  return (
    <div className="py-4 pt-20 md:pt-5 flex w-full bg-transparent relative z-50">
      <div className="relative w-full sm:w-2/3 md:w-1/2 lg:w-2/5">
        <div className="relative">
          <input
            ref={inputRef}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={handleKeyDown}
            className="w-full py-3 px-10 rounded-lg border-2 border-[#b09287] focus:outline-none focus:ring-2 focus:ring-[#4c0f2e] focus:border-[#4c0f2e] text-[#2d161e] placeholder-[#b09287] bg-[#ebe2db] shadow-sm transition-all duration-200"
            type="text"
            placeholder="Search for products..."
            aria-label="Search"
            autoComplete="off"
          />
          <FaSearch className="absolute left-3 top-3.5 text-[#4c0f2e] w-4 h-4" />
        </div>

        {searchSuggestions.length > 0 && (
          <div
            ref={dropdownRef}
            className="absolute w-full mt-2 bg-[#fefaf5] border-2 border-[#b09287] rounded-lg shadow-lg z-50 max-h-96 overflow-y-auto divide-y divide-[#e0c3b5] transition-all duration-200"
          >
            <div className="flex justify-end p-2">
              <button
                onClick={handleClear}
                className="text-sm text-[#4c0f2e] hover:text-[#2d161e] transition"
                aria-label="Close suggestions"
              >
                ✕
              </button>
            </div>

            {searchSuggestions.map((product, index) => (
              <div
                key={product._id}
                onMouseDown={() => goToProduct(product._id)}
                className={`px-4 py-3 cursor-pointer flex items-center space-x-4 transition-colors duration-150 ${
                  highlightedIndex === index ? 'bg-[#fefaf5]' : 'hover:bg-[#e0c3b5]/50'
                }`}
              >
                <div className="flex-shrink-0">
                  <img
                    src={product.image[0]}
                    alt={product.name}
                    className="w-12 h-12 object-cover rounded-md shadow-sm border border-[#b09287]"
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = 'https://via.placeholder.com/50?text=No+Image';
                    }}
                  />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-md font-bold font-forum text-[#2d161e] truncate">
                    {product.name}
                  </p>
                  <p className="text-xs text-[#b09287]">{product.category}</p>
                </div>
                <div className="flex-shrink-0">
                  <span className="text-sm font-medium text-[#4c0f2e]">
                    ₹{product.price?.toFixed(2)}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default SearchBar;
